import toInteger from 'lodash/toInteger';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync, image2Canvas, imageConverter, loadImageAsync } from '@/utils/file-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import {
    IExportBehavior,
    IImageLayerProps,
    IImageProps,
    ILayerBehavior,
    ILayerOriginInfo,
    ILayerProps,
    IRenderLayerExtraProps,
    ISize,
    IWatermarkLayerProps,
    LayerType,
} from '@/version-types';
import { IMAGE_LAYER_CANVAS_ID_PREFIX } from '@/version-types/constants/layer';
import { computeFullScreenWatermarkSize } from './utils/watermark';
import WaterMark from './views';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <WaterMark layer={layer} {...extraProps} />;
        },
        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IWatermarkLayerProps;
            data.url = base64Kit.getCode(data.url);
        },
        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IImageProps;
            deleteExtraInfo(layer);
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },

        checkDisableCollision(layer: ILayerProps) {
            if ((layer.data as IWatermarkLayerProps).isFullScreen) return true;
            return false;
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;

            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            console.time('===========生成时间');
            console.log('生成水印图片');
            const { width, height, left, top, opacity, rotate } = layer;

            const { url, isFullScreen, size } = layer.data as IWatermarkLayerProps;

            // 移动端过来的脏数据会导致下载异常
            if (!url) return;

            // 等待上传完成
            await base64Kit.getRemoteUrlAsync(url);

            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            if (isFullScreen) {
                // const baseImage = await loadImageAsync(url);
                const result = await getImgInfoAsync(url);
                const { width, height } = computeFullScreenWatermarkSize(
                    size,
                    {
                        width: result.width,
                        height: result.height,
                    },
                    {
                        width: stageWidth,
                        height: stageHeight,
                    },
                );
                const baseImage = await image2Canvas(result.img, result.width, result.height, width, height);
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
                const pat = destCtx.createPattern(baseImage, 'repeat'); //在指定的方向上重复指定的元素
                destCtx.globalAlpha = opacity;
                destCtx.fillStyle = pat!;
                destCtx.fillRect(0, 0, destCanvas.width, destCanvas.height);
                return destCanvas;
            } else {
                const domCanvas = document.getElementById(
                    `${IMAGE_LAYER_CANVAS_ID_PREFIX}${layer.id}`,
                ) as HTMLCanvasElement;
                if (!domCanvas) return;
                // 设置绘制的真正元素
                let baseImage: HTMLCanvasElement | HTMLImageElement = domCanvas;
                if (domCanvas.getAttribute('ready') === 'false') {
                    baseImage = await loadImageAsync(url);
                }
                const { width: stageWidth, height: stageHeight } = resultSize;

                const resultWidth = toInteger(width);
                const resultHeight = toInteger(height);

                const centerPoint = {
                    x: resultWidth / 2 + left,
                    y: resultHeight / 2 + top,
                };
                const drawOffsetPoint = {
                    x: -resultWidth / 2,
                    y: -resultHeight / 2,
                };
                const originImgWidth = toInteger(width);
                const originImgHeight = toInteger(height);

                const imgOffsetX = resultWidth / 2;
                const imgOffsetY = resultHeight / 2;

                // 将图层绘制到画布上
                const getStageCanvas = async (layerCanvas: HTMLCanvasElement) => {
                    // 绘制图层位移、旋转
                    const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
                    destCtx.translate(centerPoint.x, centerPoint.y);
                    destCtx.rotate((Math.PI / 180) * rotate);
                    // 透明度
                    destCtx.globalAlpha = opacity;
                    destCtx.drawImage(
                        layerCanvas,
                        0,
                        0,
                        resultWidth,
                        resultHeight,
                        drawOffsetPoint.x,
                        drawOffsetPoint.y,
                        resultWidth,
                        resultHeight,
                    );
                    return destCanvas;
                };

                //  绘制图层
                const getLayerCanvas = async () => {
                    const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);

                    destCtx.translate(imgOffsetX, imgOffsetY);

                    // destCtx.rotate(((2 * Math.PI) / 360) * rotate);
                    destCtx.drawImage(
                        baseImage,
                        -originImgWidth / 2,
                        -originImgHeight / 2,
                        originImgWidth,
                        originImgHeight,
                    );
                    return destCanvas;
                };

                const layerCanvas = await getLayerCanvas();
                const result = getStageCanvas(layerCanvas);
                console.timeEnd('===========生成时间');
                return result;
            }
        },
        checkDisableInGroup(): boolean {
            return true;
        },
        checkDisableActive(layer: ILayerProps): boolean {
            return (layer.data as IWatermarkLayerProps).isFullScreen;
        },
        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.watermark);
        },
        getMenuOverlapIcon(layer: ILayerProps) {
            const { url } = layer.data as IImageLayerProps;
            return (
                <div className="l-layer__icon border">
                    <img src={imageConverter(base64Kit.getLocalUrl(url), 32, 100)} alt="" />
                </div>
            );
        },
        checkDisableClone() {
            return true;
        },
        checkDisableCopy() {
            return true;
        },
        checkDisableCut() {
            return true;
        },
        checkDisableFlip() {
            return true;
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.WATERMARK,
    getBehavior,
};

export default exportBehavior;
